import React, { useState, useEffect } from "react";
import ReturnsOrderSelection from "./ReturnsOrderSelection";
import OrderSummary from "../shared/OrderSummary";
import TrackingInfo from "../shared/TrackingInfo";
import { ReturnStates } from "./ReturnStates";
import SingleItemReturn from "./SingleItemReturn";
import ReturnConfirmationPage from "./ReturnConfirmationPage";
import { useMockStep } from "../../hooks/useMockStep";
import TrackReturnPage from "./TrackReturnPage";
import mockOrderData from '../../mock/mockOrderData.json';
import { ReturnStatus } from "./ReturnStatus";
import ReturnPolicyLink from "./ReturnPolicyLink";
import { PiArrowLeft, PiCheck } from 'react-icons/pi';
import './ReturnWizard.css';

const RETURN_STEPS = [
  { key: ReturnStates.SELECT_RETURN_ITEMS, label: 'Items' },
  { key: ReturnStates.REASON_SELECTION, label: 'Reason' },
  { key: ReturnStates.RETURN_CONFIRMATION, label: 'Review' },
];

const ReturnWizard = ({ initialOrderDetails, customer, trackingInfo, step, setStep, shopifyProducts }) => {
  const [selectedItemToReturn, setSelectedItemToReturn] = useState(null);
  const [itemsToReturn, setItemsToReturn] = useState([]);
  const [itemIdToReturnReasons, setItemIdToReturnReasons] = useState([]);
  const [variantsToExchange, setVariantsToExchange] = useState([]);
  const [selectedExchangeVariant, setSelectedExchangeVariant] = useState(null);
  const [selectedExchangeProduct, setSelectedExchangeProduct] = useState(null);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [orderDetails, setOrderDetails] = useState(initialOrderDetails);
  const mockStep = useMockStep();

  const isReturnFlowStep = RETURN_STEPS.some(s => s.key === step);
  const currentStepIdx = RETURN_STEPS.findIndex(s => s.key === step);

  useEffect(() => {
    if (mockStep) {
      setStep(mockStep);
      setSelectedReturn(mockOrderData.orderReturns[1]);
      setSelectedItemToReturn(mockOrderData.line_items[0]);
      setItemsToReturn([{ ...mockOrderData.line_items[1] }]);
      setVariantsToExchange([mockOrderData.shopifyProducts[1].variants[0]]);
    }
  }, [mockStep]); // Only depend on mockStep

  // Handle step changes separately
  useEffect(() => {

    if (!mockStep) {
      if (step === ReturnStates.RETURN_CONFIRMATION) {

        if (selectedItemToReturn) {
          // Add exchange variant only if it doesn't already exist (do this first, regardless of return item logic)
          if (selectedExchangeVariant) {
            setVariantsToExchange(prev => {
                const existingVariantIndex = prev.findIndex(variant => variant.id === selectedExchangeVariant.id);
                if (existingVariantIndex === -1) {
                // Variant doesn't exist, add it
                return [...prev, selectedExchangeVariant];
                }
                // Variant already exists, add 1 to quantity
                const updated = [...prev];
                updated[existingVariantIndex] = {
                ...updated[existingVariantIndex],
                quantity: (updated[existingVariantIndex].quantity || 1) + 1
                };
                return updated;
            });
          }

          setItemsToReturn(prev => {
            const existingIndex = prev.findIndex(item => item.id === selectedItemToReturn.id);
            const returnType = selectedExchangeVariant ? "EXCHANGE" : "REFUND";

            // It's already in the list — update the return quantity
            if (existingIndex !== -1) {
              const updated = [...prev];
              updated[existingIndex] = {
                ...updated[existingIndex],
                returnQuantity: updated[existingIndex].returnQuantity + selectedItemToReturn.returnQuantity,
                returnType: returnType
              };
              return updated;
            }

            return [...prev, { ...selectedItemToReturn, returnType: returnType }];
          });

          setSelectedExchangeVariant(null);
          setSelectedItemToReturn(null);
        }
      }

      if (step === ReturnStates.ORDER_SUMMARY) {
        setSelectedExchangeVariant(null);
        setSelectedItemToReturn(null);
        setItemsToReturn([]);
        setVariantsToExchange([]);
        setItemIdToReturnReasons([]);
      }
    }

  }, [step, mockStep, selectedItemToReturn, selectedExchangeVariant]);

  const handleStartReturn = () => {
    setStep(ReturnStates.SELECT_RETURN_ITEMS);
  };

  const renderStepper = () => (
    <div className="returns-stepper">
      {RETURN_STEPS.map((s, i) => (
        <React.Fragment key={s.key}>
          {i > 0 && (
            <div className={`stepper-line ${i <= currentStepIdx ? 'completed' : ''}`} />
          )}
          <div className="stepper-step">
            <div className={`stepper-circle ${i === currentStepIdx ? 'active' : ''} ${i < currentStepIdx ? 'completed' : ''}`}>
              {i + 1}
            </div>
            <span className={`stepper-label ${i === currentStepIdx ? 'active' : ''}`}>
              {s.label}
            </span>
          </div>
        </React.Fragment>
      ))}
    </div>
  );

  if (isReturnFlowStep) {
    return (
      <div className="returns-flow-container">
        <button
          className="returns-back-btn"
          onClick={() => setStep(ReturnStates.ORDER_SUMMARY)}
        >
          <PiArrowLeft size={16} /> Back
        </button>

        {renderStepper()}

        {step === ReturnStates.SELECT_RETURN_ITEMS && (
          <ReturnsOrderSelection
            orderDetails={orderDetails}
            itemsToReturn={itemsToReturn}
            onStepChange={setStep}
            selectedItem={selectedItemToReturn}
            setSelectedItem={setSelectedItemToReturn}
            shopifyProducts={shopifyProducts}
            flowType="RETURN"
          />
        )}

        {step === ReturnStates.REASON_SELECTION && (
          <SingleItemReturn
            selectedItem={selectedItemToReturn}
            selectedVariant={selectedExchangeVariant}
            setSelectedVariant={setSelectedExchangeVariant}
            setItemIdToReturnReasons={setItemIdToReturnReasons}
            onStepChange={setStep}
            shopifyProducts={shopifyProducts}
            setSelectedExchangeProduct={setSelectedExchangeProduct}
            selectedExchangeProduct={selectedExchangeProduct}
            orderDetails={orderDetails}
          />
        )}

        {step === ReturnStates.RETURN_CONFIRMATION && (
          <ReturnConfirmationPage
            orderDetails={orderDetails}
            itemsToReturn={itemsToReturn}
            variantsToExchange={variantsToExchange}
            itemIdToReturnReasons={itemIdToReturnReasons}
            customer={customer}
            onStepChange={setStep}
            shopifyProducts={shopifyProducts}
            updateOrderDetails={setOrderDetails}
            flowType="RETURN"
          />
        )}
      </div>
    );
  }

  return (
    <>
      {step === ReturnStates.ORDER_TRACKING && (
        <TrackingInfo
          trackingInfo={trackingInfo}
          onBack={() => setStep(ReturnStates.ORDER_SUMMARY)}
        />
      )}
      {step === ReturnStates.RETURN_TRACKING && (
        <TrackReturnPage
          orderDetails={orderDetails}
          orderReturn={selectedReturn}
          customer={customer}
          shopifyProducts={shopifyProducts}
          onBack={() => setStep(ReturnStates.ORDER_SUMMARY)}
        />
      )}

      {step === ReturnStates.ORDER_SUMMARY && (
        <OrderSummary
          orderDetails={orderDetails}
          updateOrderDetails={setOrderDetails}
          customer={customer}
          trackingInfo={trackingInfo}
          setSelectedReturn={setSelectedReturn}
          onStepChange={setStep}
          shopifyProducts={shopifyProducts}
          mode="returns"
          buttonText="Start Return or Exchange"
          onButtonClick={handleStartReturn}
          orderReturns={orderDetails.orderReturns || []}
          ReturnStatus={ReturnStatus}
          ReturnPolicyLink={ReturnPolicyLink}
        />
      )}
    </>
  );
};

export default ReturnWizard;
